import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";

import { useInsightsWs } from "./Utilities/CoTraderUtils";
import LoadingComponent from "../Common/LoadingComponent";
import { InsightTile } from "./InsightTile";
import CoTraderBarChart from "./CoTraderChart/CoTraderBarChart";
import CoTraderExecutionDecision from "./CoTraderExecutionDecision";
import CoTraderCustomTickerTape from "../TradingView/CoTraderTickerTape";
import TradingViewChartMobile from "../TradingView/TradingViewChartMobile";
import MobileHeader from "./MobileHeader";
import CoTraderMobileFiltersBar from "./MbCoTraderFilter";
import CoTraderEmgptDivMobile from "./CoTraderEmgptDivMobile";
import CoTraderDemoLockedChatPanel from "./CoTraderDemoLockedChatPanel";
import CoTraderLiveSentimentBar from "./CoTraderLiveSentimentBar";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import { useStablePrependScroll } from "./useStablePrependScroll";
import {
  COTRADER_LANGUAGE_LIST,
  normalizeCoTraderLanguage,
} from "./Utilities/languages";

const CoTraderMobile = ({
  hideTopChrome = false,
  isDemoMode = false,
  instrumentTypeOverride = null,
  languageOverride = null,
  selectedSymbolOverride = null,
  showSuggestionsOverride = null,
}) => {
  const pageTitle = "FX CoTrader";
  const [updateSpeed, setUpdateSpeed] = useState(2);
  const [selectedSymbol, setSelectedSymbol] = useState(
    selectedSymbolOverride || localStorage.getItem("default_symbol") || "USDJPY"
  );

  const [tenor, setTenor] = useState([]);
  const [tenorList, setTenorList] = useState([]);

  const showCountDownInsights = true;
  const selectedSymbolRef = useRef(selectedSymbol);

  const [prices, setPrices] = useState([]);

  const [searchFlag, setSearchFlag] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const [language, setLanguage] = useState(() =>
    normalizeCoTraderLanguage(
      languageOverride || localStorage.getItem("user_language"),
    ),
  );
  const [languageKey, setLanguageKey] = useState(0);

  const [tabs, setTabs] = useState([]);
  const [activeTvSymbol, setActiveTvSymbol] = useState(null);

  const [instrumentType, setInstrumentType] = useState(
    instrumentTypeOverride || "FX_SPOT",
  );

  const [tvTickers, setTvTickers] = useState([
    "AAPL",
    "MSFT",
    "GOOGL",
    "AMZN",
    "TSLA",
    "META",
    "EURRON=X",
    "USDINR=X",
    "USDNOK=X",
    "USDCNY=X",
    "USDSEK=X",
    "USDCLP=X",
    "USDJPY=X",
    "USDCOP=X",
    "USDMYR=X",
    "EURHUF=X",
    "USDBRL=X",
    "GBPUSD=X",
    "USDARS=X",
    "USDPEN=X",
    "EURUSD=X",
    "USDKRW=X",
    "NZDUSD=X",
    "USDTWD=X",
    "USDTHB=X",
    "USDMAD=X",
    "AUDUSD=X",
    "USDHKD=X",
    "USDPHP=X",
    "EURPLN=X",
    "USDTRY=X",
    "USDCAD=X",
    "USDMXN=X",
    "USDZAR=X",
    "USDCHF=X",
    "USDIDR=X",
  ]);

  // Bottom-half tabs: Updates | Algo vs RFQ | FX GPT
  const [activeBottomTab, setActiveBottomTab] = useState("updates");

  // ===== Theme (read from localStorage + sync with theme toggle event) =====


  const {isDark} = useTheme();

  const styles = useMemo(() => {
    if (isDark) {
      return {
        pageBg: "#0B0F1A",
        surface: "#12172A",
        border: "#1E2642",
        text: "#E8ECF8",
        muted: "#A6B0CF",
        pillActiveBg: "#4C7DFF",
        pillActiveText: "#FFFFFF",
        pillIdleBg: "transparent",
        pillIdleText: "#A6B0CF",
        pillIdleBorder: "#232B4A",
      };
    }
    return {
      pageBg: "#FFFFFF",
      surface: "#FFFFFF",
      border: "#E9ECEF",
      text: "#0B1220",
      muted: "#5B6472",
      pillActiveBg: "#0D6EFD",
      pillActiveText: "#FFFFFF",
      pillIdleBg: "transparent",
      pillIdleText: "#0D6EFD",
      pillIdleBorder: "#0D6EFD",
    };
  }, [isDark]);
  const GptPanelComponent = isDemoMode
    ? CoTraderDemoLockedChatPanel
    : CoTraderEmgptDivMobile;

  const languageList = COTRADER_LANGUAGE_LIST;

  const onSymbolClick = (symbol) => {
    const a = symbol.split("=");
    if (a.length > 1) setSelectedSymbol(a[0]);
    else setActiveTvSymbol(symbol);
  };

  useEffect(() => {
    if (
      typeof selectedSymbolOverride === "string" &&
      selectedSymbolOverride &&
      selectedSymbolOverride !== selectedSymbol
    ) {
      setSelectedSymbol(selectedSymbolOverride);
    }
  }, [selectedSymbol, selectedSymbolOverride]);

  useEffect(() => {
    if (
      typeof languageOverride === "string" &&
      languageOverride &&
      normalizeCoTraderLanguage(languageOverride) !== language
    ) {
      setLanguage(normalizeCoTraderLanguage(languageOverride));
      setLanguageKey((prev) => prev + 1);
    }
  }, [language, languageOverride]);

  useEffect(() => {
    if (localStorage.getItem("user_language") !== language) {
      localStorage.setItem("user_language", language);
    }
  }, [language]);

  useEffect(() => {
    if (
      typeof instrumentTypeOverride === "string" &&
      instrumentTypeOverride &&
      instrumentTypeOverride !== instrumentType
    ) {
      setInstrumentType(instrumentTypeOverride);
    }
  }, [instrumentType, instrumentTypeOverride]);

  useEffect(() => {
    if (
      typeof showSuggestionsOverride === "boolean" &&
      showSuggestionsOverride !== showSuggestions
    ) {
      setShowSuggestions(showSuggestionsOverride);
    }
  }, [showSuggestions, showSuggestionsOverride]);

  const { insights, loading, sentiment, sentimentUpdatedAt } = useInsightsWs(
    selectedSymbol,
    updateSpeed,
    selectedSymbolRef,
    showCountDownInsights,
    language,
    1200,
    setPrices,
    { isDemoMode },
  );
  const updatesScrollRef = useStablePrependScroll({
    itemCount: insights.length,
    enabled: activeBottomTab === "updates" && !loading,
    resetKey: `${selectedSymbol}:${language}`,
  });

  const fxComponent = {
    FX_SPOT: (
      <TradingViewChartMobile
        symbol={selectedSymbol}
        titleOverride={selectedSymbol}
        dark={isDark}
        isDemoMode={isDemoMode}
      />
    ),
    FX_FORWARD: (
      <CoTraderBarChart
        selectedSymbol={selectedSymbol}
        tenor={tenor}
        setTenorList={setTenorList}
        isDark={isDark}
      />
    ),
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle} | EMAlpha</title>
      </Helmet>

      <div
        className="w-100 h-100 d-flex flex-column"
        key={languageKey}
        style={{ background: styles.pageBg }}
      >
        {/* Mobile Header (theme toggle lives here now) */}
        {!hideTopChrome && <MobileHeader />}

        {/* Ticker */}
        {!hideTopChrome && (
          <div className="px-2 pt-2">
            <CoTraderCustomTickerTape
              symbols={tvTickers}
              dark={isDark}
              onSymbolClick={onSymbolClick}
            />
          </div>
        )}

        {!hideTopChrome && (
          <CoTraderMobileFiltersBar
            selectedSymbol={selectedSymbol}
            prices={prices}
            setSelectedSymbol={setSelectedSymbol}
            setUpdateSpeed={setUpdateSpeed}
            tenorList={tenorList}
            setTenor={instrumentType === "FX_FORWARD" ? setTenor : null}
            setInstrumentType={setInstrumentType}
            instrumentType={instrumentType}
            showSuggestions={showSuggestions}
            setShowSuggestions={setShowSuggestions}
            language={language}
            setLanguage={setLanguage}
            languageList={languageList}
            setLanguageKey={setLanguageKey}
            isDark={isDark}
          />
        )}

        {!hideTopChrome && (
          <div
            className="px-3 py-2"
            style={{
              background: styles.surface,
              borderBottom: `1px solid ${styles.border}`,
            }}
          >
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                color: styles.text,
                lineHeight: 1.2,
              }}
            >
              {pageTitle}
            </div>
            <div
              style={{
                fontSize: "0.8rem",
                fontWeight: 500,
                color: styles.muted,
                lineHeight: 1.2,
                marginTop: "0.2rem",
              }}
            >
              {selectedSymbol}
            </div>
          </div>
        )}

        {/* ======= MAIN SPLIT: Chart (top) + Bottom Tabs (bottom) ======= */}
        <div className="flex-grow-1 d-flex flex-column overflow-hidden">
          {/* Top: Chart */}
          <div
            style={{
              flex: "0 0 30%",
              minHeight: 0,
              borderBottom: `1px solid ${styles.border}`,
              background: styles.surface,
            }}
          >
            <div className="h-100 w-100 p-1">
              {/* pass isDark if you want, but charts already read theme internally now */}
              {fxComponent[instrumentType]}
            </div>
          </div>

          {/* Bottom: Tabs + Content */}
          <div className="d-flex flex-column" style={{ flex: "1 1 52%", minHeight: 0 }}>
            {/* Bottom Tabs Header */}
            <div
              className="px-3 py-2 d-flex gap-2 flex-wrap"
              style={{
                borderBottom: `1px solid ${styles.border}`,
                background: styles.surface,
              }}
            >
              <button
                className="btn btn-sm rounded-pill"
                style={{
                  background:
                    activeBottomTab === "updates" ? styles.pillActiveBg : styles.pillIdleBg,
                  color:
                    activeBottomTab === "updates" ? styles.pillActiveText : styles.pillIdleText,
                  border:
                    activeBottomTab === "updates"
                      ? "1px solid transparent"
                      : `1px solid ${styles.pillIdleBorder}`,
                }}
                onClick={() => setActiveBottomTab("updates")}
                type="button"
              >
                Updates
              </button>

              <button
                className="btn btn-sm rounded-pill"
                style={{
                  background:
                    activeBottomTab === "execution"
                      ? styles.pillActiveBg
                      : styles.pillIdleBg,
                  color:
                    activeBottomTab === "execution"
                      ? styles.pillActiveText
                      : styles.pillIdleText,
                  border:
                    activeBottomTab === "execution"
                      ? "1px solid transparent"
                      : `1px solid ${styles.pillIdleBorder}`,
                }}
                onClick={() => setActiveBottomTab("execution")}
                type="button"
              >
                Algo vs RFQ
              </button>

              <button
                className="btn btn-sm rounded-pill"
                style={{
                  background:
                    activeBottomTab === "fxgpt" ? styles.pillActiveBg : styles.pillIdleBg,
                  color:
                    activeBottomTab === "fxgpt" ? styles.pillActiveText : styles.pillIdleText,
                  border:
                    activeBottomTab === "fxgpt"
                      ? "1px solid transparent"
                      : `1px solid ${styles.pillIdleBorder}`,
                }}
                onClick={() => setActiveBottomTab("fxgpt")}
                type="button"
              >
                FX GPT
              </button>

            </div>

            <CoTraderLiveSentimentBar
              isDark={isDark}
              sentiment={sentiment}
              sentimentUpdatedAt={sentimentUpdatedAt}
              symbolKey={selectedSymbol}
            />

            {/* Bottom Content Scroll Area */}
            <div
              ref={activeBottomTab === "updates" ? updatesScrollRef : null}
              className="flex-grow-1 overflow-auto"
              style={{ minHeight: 0, background: styles.pageBg }}
            >
              {activeBottomTab === "updates" && (
                <div className="p-3 d-flex flex-column">
                  {loading ? (
                    <LoadingComponent />
                  ) : insights?.length > 0 ? (
                    insights.map((x, i) => (
                      <InsightTile
                        key={i}
                        title={x.title}
                        timeStamp={x.timestamp}
                        type={x.type}
                        description={x.description}
                        startTime={x.start_date}
                        isDark={isDark} // optional if you theme the tile
                        impactScore={x.impact_score}
                        question={x.question}
                      />
                    ))
                  ) : (
                    <div className="text-center" style={{ color: styles.muted }}>
                      Generating the Updates...
                    </div>
                  )}
                </div>
              )}

              {activeBottomTab === "execution" && (
                <div className="p-3">
                  <CoTraderExecutionDecision
                    currencyPair={selectedSymbol}
                    language={language}
                    isDark={isDark}
                  />
                </div>
              )}

              {activeBottomTab === "fxgpt" && (
                <div className="h-100" style={{ minHeight: 0 }}>
                  <GptPanelComponent
                    selectedSymbol={selectedSymbol}
                    searchFlag={searchFlag}
                    setSearchFlag={setSearchFlag}
                    showSuggestions={showSuggestions}
                    setShowSuggestions={setShowSuggestions}
                    tabs={tabs}
                    setTabs={setTabs}
                    useFxSuggestedQuestionsApi
                    setTvTickers={setTvTickers}
                    activeTvSymbol={activeTvSymbol}
                    setActiveTvSymbol={setActiveTvSymbol}
                    isDark={isDark} // optional if you theme emgpt div
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Settings Bottom Sheet - if you add later */}
      </div>
    </>
  );
};

export default CoTraderMobile;
